<template>
    <section class="zigzag-section">
        <div class="zigzag-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />

            <div class="zigzag__list">
                <article v-for="(cell, idx) in props.item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="zigzag__item"
                    :class="{ 'zigzag__item--reverse': idx % 2 === 1 }">
                    <!-- <div class="zigzag__item__media" @click="handleCellClick(cell)">
                        <img v-if="getCellImage(cell)" :src="getCellImage(cell)" :alt="getCellTitle(cell)"
                            loading="lazy" />
                    </div> -->
                    <MediaCard v-if="getCellImage(cell)" class="zigzag__item__media" :url="getCellImage(cell)"
                        :name="getCellTitle(cell)" video-popup />
                    <div class="zigzag__item__content">
                        <h3 v-if="getCellTitle(cell)" class="zigzag__item__title">{{ getCellTitle(cell) }}</h3>
                        <p v-if="getCellDesc(cell)" class="zigzag__item__desc">{{ getCellDesc(cell) }}</p>

                        <!-- Hyperlink note line -->
                        <p v-if="getHrefAll(cell)" class="zigzag__item__note">
                            <template v-if="getHrefText(cell) && getHref(cell)">
                                <span>{{ getTextBefore(cell) }}</span>
                                <a class="zigzag__item__link" @click.stop="handleLinkClick(cell)">{{ getHrefText(cell)
                                    }}</a>
                                <span>{{ getTextAfter(cell) }}</span>
                            </template>
                            <template v-else-if="getHref(cell)">
                                <a class="zigzag__item__link" @click.stop="openHref(getHref(cell))">{{ getHrefAll(cell)
                                    }}</a>
                            </template>
                            <template v-else>
                                {{ getHrefAll(cell) }}
                            </template>
                        </p>

                        <!-- Buttons -->
                        <div v-if="hasButtons(cell)" class="zigzag__item__btns">
                            <button v-if="getMainBtn(cell)" class="zigzag__btn zigzag__btn--primary"
                                @click.stop="handleMainBtnClick(cell)">
                                {{ getMainBtn(cell) }}
                            </button>
                            <button v-if="getBtn(cell)" class="zigzag__btn zigzag__btn--secondary"
                                @click.stop="handleBtnClick(cell)">
                                {{ getBtn(cell) }}
                            </button>
                        </div>
                    </div>
                </article>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

// const emit = defineEmits<{
//     (e: 'click', item: Module, cell?: unknown): void
// }>()

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getCellDesc(cell: unknown): string {
    return getStrWithRecord(cell, 'subTitle')
}
// function getCellUrl(cell: unknown): string {
//     return getStrWithRecord(cell, 'url')
// }
function getMainBtn(cell: unknown): string {
    return getStrWithRecord(cell, 'mainBtn')
}
function getMainUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'mainUrl')
}
function getBtn(cell: unknown): string {
    return getStrWithRecord(cell, 'btn')
}
function getBtnUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'btnUrl')
}
function getHref(cell: unknown): string {
    return getStrWithRecord(cell, 'href')
}
function getHrefText(cell: unknown): string {
    return getStrWithRecord(cell, 'hrefText')
}
function getHrefAll(cell: unknown): string {
    return getStrWithRecord(cell, 'hreftAll')
}

function hasButtons(cell: unknown): boolean {
    return !!(getMainBtn(cell) || getBtn(cell))
}

function getTextBefore(cell: unknown): string {
    const all = getHrefAll(cell)
    const linkText = getHrefText(cell)
    if (!all || !linkText) return ''
    const idx = all.indexOf(linkText)
    return idx >= 0 ? all.substring(0, idx) : all
}

function getTextAfter(cell: unknown): string {
    const all = getHrefAll(cell)
    const linkText = getHrefText(cell)
    if (!all || !linkText) return ''
    const idx = all.indexOf(linkText)
    return idx >= 0 ? all.substring(idx + linkText.length) : ''
}

function openHref(url: string): void {
    if (url) jumpToUrl(url)
}

// function handleCellClick(cell: unknown): void {
//     emit('click', props.item, cell)
//     const url = getCellUrl(cell)
//     if (url) jumpToUrl(url)
// }

function handleLinkClick(cell: unknown): void {
    openHref(getHref(cell))
}

function handleMainBtnClick(cell: unknown): void {
    const url = getMainUrl(cell)
    if (url) jumpToUrl(url)
}

function handleBtnClick(cell: unknown): void {
    const url = getBtnUrl(cell)
    if (url) jumpToUrl(url)
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$primary: #ff6d00;
$primary-hover: #ff8533;
$link-color: #2563eb;

.zigzag-section {
    width: 100%;
}

.zigzag-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.zigzag__list {
    display: flex;
    flex-direction: column;
    gap: 3rem;

    @media (min-width: $breakpoint-md) {
        gap: 5rem;
    }
}

.zigzag__item {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;

    @media (min-width: $breakpoint-md) {
        flex-direction: row;
        align-items: center;
        gap: 4rem;
    }

    &--reverse {
        @media (min-width: $breakpoint-md) {
            flex-direction: row-reverse;
        }
    }

    &__media {
        flex-shrink: 0;
        width: 100%;
        border-radius: 1.25rem;
        overflow: hidden;
        background: $color-bg-tertiary;

        @media (min-width: $breakpoint-md) {
            width: 55%;
            border-radius: 1.5rem;
            aspect-ratio: 16 / 11;
        }

        :deep(.card) {
            width: 100%;
            height: 100%;
            aspect-ratio: 4 / 3;

            @media (min-width: $breakpoint-md) {
                aspect-ratio: auto;
                height: 100%;
            }
        }
    }

    &__content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        @media (min-width: $breakpoint-md) {
            gap: 1rem;
        }
    }

    &__title {
        font-size: 1.375rem;
        font-weight: 800;
        color: $color-text;
        margin: 0;
        line-height: 1.3;
        letter-spacing: -0.02em;

        @media (min-width: $breakpoint-md) {
            font-size: 2.25rem;
        }
    }

    &__desc {
        font-size: 0.875rem;
        color: $color-text-secondary;
        margin: 0;
        line-height: 1.7;

        @media (min-width: $breakpoint-md) {
            font-size: 1.125rem;
            line-height: 1.8;
            color: #555;
        }
    }

    &__note {
        font-size: 0.8125rem;
        color: $color-text-secondary;
        margin: 0;
        line-height: 1.6;

        @media (min-width: $breakpoint-md) {
            font-size: 1rem;
            margin-top: 0.25rem;
        }
    }

    &__link {
        color: $link-color;
        cursor: pointer;
        font-weight: 500;
        transition: color 0.15s;

        &:hover {
            color: #1d4ed8;
            text-decoration: underline;
        }
    }

    &__btns {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        margin-top: 0.75rem;

        @media (min-width: $breakpoint-md) {
            gap: 1.25rem;
            margin-top: 1.25rem;
        }
    }
}

.zigzag__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem 2rem;
    font-size: 0.9375rem;
    font-weight: 600;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
    white-space: nowrap;

    @media (min-width: $breakpoint-md) {
        padding: 0.75rem 2.5rem;
        font-size: 1.125rem;
    }

    &--primary {
        background: $primary;
        color: #fff;

        &:hover {
            background: $primary-hover;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(255, 109, 0, 0.3);
        }
    }

    &--secondary {
        background: #f0f0f0;
        color: $color-text;

        &:hover {
            background: #e5e5e5;
        }
    }
}
</style>
